import React from 'react';
import { Link } from 'react-router-dom';
import { useFormContext, useFieldArray } from 'react-hook-form';
import styled from 'styled-components';
import { FaPlus, FaTimes, FaTrash } from 'react-icons/fa';

import { DetailFormSelect, DetailFormValue } from 'components/DetailsView';
import {
    ConsumptionColumnInput,
    GetMicrostoreColumnsOfOrganizationQuery,
    PriceOptionType,
    useGetProductPriceRangesQuery,
} from 'data/__generated__';
import { BasicInput } from 'components/BasicInput';
import { DEFAULT_B2B_RANGE_ID } from 'constants/prices';

export const ConsumptionConfigurationDetails: React.FC<{
    microstoreColumns: GetMicrostoreColumnsOfOrganizationQuery['microstoreColumnsOfOrganization'];
    B2BPriceRangeId?: string | null;
}> = ({ microstoreColumns, B2BPriceRangeId }) => {
    const { data: priceRangesData } = useGetProductPriceRangesQuery();
    const effectiveRangeId = B2BPriceRangeId ?? DEFAULT_B2B_RANGE_ID;
    const priceRangeName =
        priceRangesData?.productPriceRanges?.find((r) => r._id === effectiveRangeId)?.name ?? 'Prix B2B';
    const { control, register, watch } = useFormContext<{
        invoiceSettings: { consumptionColumns: ConsumptionColumnInput[] };
    }>();

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: 'invoiceSettings.consumptionColumns',
    });

    const machineOptions = [
        { value: null, label: 'Toutes les machines' },
        ...microstoreColumns.map((column) => ({
            value: column._id,
            label: column.name,
        })),
    ];

    const priceTypeOptions = [
        { value: PriceOptionType.DefaultPricing, label: 'Tarif unique' },
        { value: PriceOptionType.CupPricing, label: 'Tarif avec gobelet' },
        { value: PriceOptionType.MultiplePricing, label: 'Tarif multiple' },
    ];

    const tvaOptions = [
        { value: null, label: '0%' },
        { value: 0.055, label: '5.5%' },
        { value: 0.1, label: '10%' },
        { value: 0.2, label: '20%' },
    ];

    return (
        <>
            <SectionTitle>Facturation à la transaction</SectionTitle>
            {fields.map((column, columnIndex) => {
                const priceOptionType = watch(`invoiceSettings.consumptionColumns.${columnIndex}.priceOptionType`);
                const currentColumn = watch(`invoiceSettings.consumptionColumns.${columnIndex}`);
                const showWithoutCupPrice = priceOptionType === PriceOptionType.CupPricing;
                const showTiers = priceOptionType !== PriceOptionType.MultiplePricing;

                return (
                    <Card key={column.id}>
                        <MachineSection>
                            <DetailFormSelect
                                isMulti
                                label="Machines"
                                placeholder="Sélectionner une ou plusieurs machines"
                                name={`invoiceSettings.consumptionColumns.${columnIndex}.microstoreColumnIds`}
                                options={machineOptions}
                            />
                        </MachineSection>
                        <ConfigSection>
                            <DetailFormSelect
                                label="TVA"
                                placeholder="Sélectionner une TVA"
                                name={`invoiceSettings.consumptionColumns.${columnIndex}.tva`}
                                options={tvaOptions}
                            />
                        </ConfigSection>
                        <ConfigSection>
                            <DetailFormSelect
                                label="Type de prix"
                                placeholder="Sélectionner un type"
                                name={`invoiceSettings.consumptionColumns.${columnIndex}.priceOptionType`}
                                options={priceTypeOptions}
                            />
                        </ConfigSection>

                        {!showTiers && (
                            <InfoMessage>
                                La facturation multiple utilise la gamme de prix de l'organisation.{' '}
                                <PriceRangeLink
                                    to={`/product-price-range/${effectiveRangeId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {priceRangeName}
                                </PriceRangeLink>{' '}
                            </InfoMessage>
                        )}

                        {showTiers && (
                            <TiersSection>
                                <TiersHeaderRow>
                                    <TiersHeaderLabel>Palier minimum</TiersHeaderLabel>
                                    <TiersHeaderLabel>Prix unitaire</TiersHeaderLabel>
                                    {showWithoutCupPrice && <TiersHeaderLabel>Prix sans gobelet</TiersHeaderLabel>}
                                </TiersHeaderRow>

                                <TierRow>
                                    <BasicInput type="text" value="Prix de base" disabled width="120px" />
                                    <DetailFormValue
                                        type="number"
                                        placeholder="Prix (€)"
                                        step="0.0001"
                                        min="0"
                                        width="120px"
                                        {...register(
                                            `invoiceSettings.consumptionColumns.${columnIndex}.priceTiers.0.unitPrice`,
                                            {
                                                valueAsNumber: true,
                                            },
                                        )}
                                    />
                                    {showWithoutCupPrice && (
                                        <DetailFormValue
                                            type="number"
                                            placeholder="Prix sans gobelet (€)"
                                            step="0.0001"
                                            min="0"
                                            width="120px"
                                            {...register(
                                                `invoiceSettings.consumptionColumns.${columnIndex}.priceTiers.0.unitPriceWithoutCup`,
                                                {
                                                    valueAsNumber: true,
                                                },
                                            )}
                                        />
                                    )}
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            update(columnIndex, {
                                                ...currentColumn,
                                                priceTiers: [
                                                    ...(currentColumn.priceTiers || []),
                                                    { thresholdMin: 1, unitPrice: 0, unitPriceWithoutCup: 0 },
                                                ],
                                            });
                                        }}
                                    >
                                        <FaPlus />
                                    </Button>
                                </TierRow>

                                {column.priceTiers.slice(1).map((tier, tierIndex) => (
                                    <TierRow key={tierIndex}>
                                        <DetailFormValue
                                            type="number"
                                            placeholder="Seuil"
                                            width="120px"
                                            min="1"
                                            {...register(
                                                `invoiceSettings.consumptionColumns.${columnIndex}.priceTiers.${tierIndex + 1}.thresholdMin`,
                                                {
                                                    valueAsNumber: true,
                                                },
                                            )}
                                        />
                                        <DetailFormValue
                                            type="number"
                                            placeholder="Prix (€)"
                                            step="0.0001"
                                            width="120px"
                                            min="0"
                                            {...register(
                                                `invoiceSettings.consumptionColumns.${columnIndex}.priceTiers.${tierIndex + 1}.unitPrice`,
                                                {
                                                    valueAsNumber: true,
                                                },
                                            )}
                                        />
                                        {showWithoutCupPrice && (
                                            <DetailFormValue
                                                type="number"
                                                placeholder="Prix sans gobelet (€)"
                                                step="0.0001"
                                                width="120px"
                                                min="0"
                                                {...register(
                                                    `invoiceSettings.consumptionColumns.${columnIndex}.priceTiers.${tierIndex + 1}.unitPriceWithoutCup`,
                                                    {
                                                        valueAsNumber: true,
                                                    },
                                                )}
                                            />
                                        )}
                                        <SmallButton
                                            type="button"
                                            onClick={() => {
                                                update(columnIndex, {
                                                    ...currentColumn,
                                                    priceTiers: (currentColumn.priceTiers || []).filter(
                                                        (_, i) => i !== tierIndex + 1,
                                                    ),
                                                });
                                            }}
                                        >
                                            <FaTrash />
                                        </SmallButton>
                                    </TierRow>
                                ))}
                            </TiersSection>
                        )}
                        <DeleteButton type="button" onClick={() => remove(columnIndex)}>
                            <FaTimes />
                        </DeleteButton>
                    </Card>
                );
            })}

            <AddColumnSection>
                <Button
                    type="button"
                    onClick={() =>
                        append({
                            microstoreColumnIds: [],
                            tva: 0.1,
                            priceOptionType: PriceOptionType.DefaultPricing,
                            priceTiers: [{ thresholdMin: null, unitPrice: 0, unitPriceWithoutCup: 0 }],
                        })
                    }
                >
                    <FaPlus />
                </Button>
            </AddColumnSection>
        </>
    );
};

const Card = styled.div`
    padding: 5px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const Button = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    color: white;
    cursor: pointer;
    flex-shrink: 0;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    font-size: 16px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-left: auto;

    &:hover {
        background-color: ${({ theme }) => `${theme.errorColor}20`};
    }
`;

const SmallButton = styled(Button)`
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
`;

const MachineSection = styled.section`
    display: flex;
    flex-direction: column;
    width: 260px;
    min-width: 260px;
`;

const ConfigSection = styled.section`
    display: flex;
    flex-direction: column;
    width: 160px;
    min-width: 160px;
`;

const TiersSection = styled.section`
    margin: 0;
`;

const TiersHeaderRow = styled.header`
    display: flex;
    gap: 10px;
`;

const TiersHeaderLabel = styled.span`
    width: 120px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
    margin-bottom: 5px;
`;

const TierRow = styled.section`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 5px;
`;

const AddColumnSection = styled.section`
    margin-bottom: 5px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.textColor};
    font-size: 16px;
    font-weight: 600;
`;

const InfoMessage = styled.div`
    color: ${({ theme }) => theme.textColor};
    font-size: 12px;
    opacity: 0.7;
    align-self: center;
`;

const PriceRangeLink = styled(Link)`
    color: ${({ theme }) => theme.ctaPrimaryHoveredColor};
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;
